/**
 * Carrying the selected vessel across the iframe → new-tab boundary.
 *
 * The SIYA chat embeds this shell in an iframe for its "Micro Apps" panel, and its
 * open-in-new-tab control loads the same shell as a TOP-LEVEL page. Safari does not
 * hand that new tab the storage the iframe wrote: third-party storage is partitioned
 * by top-level site, so `vesselSync_activeVessel` written inside the panel lands in
 * the chat's partition, while the new tab reads the shell's own — which still holds
 * whatever vessel was selected the last time the shell was opened standalone, or
 * nothing at all. The tab therefore opens on a DIFFERENT vessel than the one on
 * screen a second earlier. Chromium shares the bucket here, which is why this only
 * reproduces in Safari.
 *
 * The route is supposed to survive that hop by travelling in the URL —
 * saveLastVisitedRoute posts it to the parent, which appends it as `?lastVisitedRoute=`
 * on the new-tab URL (see utils/lastVisitedRoute). The vessel rides that same channel
 * rather than needing one of its own: the route posted upward carries the vessel in its
 * query, so the value comes back as `?lastVisitedRoute=/class%3FvesselId%3D…`. Explicit
 * top-level `?vesselId=…&imoList=…` params are read too, so the chat can append them
 * directly if it prefers a dedicated vessel handover.
 *
 * Both forms are read here because neither arrives yet: the deployed SIYA SPA opens the
 * new tab from a plain <a href> built off its static `microapps` URL template and has no
 * listener for the posted route at all. This side is ready for whichever it sends.
 *
 * Everything here runs before React mounts (initVesselBridge, module scope in _app),
 * which is what makes it work: the shared VesselDropdown restores its selection from
 * localStorage in a mount effect, so the keys are already correct by the time it looks.
 */
import { reportError } from '@/observability/clientLogger';


/** Param names the vessel travels under — the same vocabulary OptimizedIframe sends to Apps. */
export const VESSEL_PARAMS = ['vesselId', 'vesselName', 'vesselLevel', 'imoList'] as const;

export interface VesselSnapshot {
  id: string;
  vesselName: string;
  imo: string;
  imoList: string[];
  level: string;
}

/** The levels the shared VesselDropdown understands; anything else is not trustworthy input. */
const LEVELS = ['VESSEL', 'GROUP', 'CUSTOM_GROUP'];

/**
 * A snapshot from query params, or null when they are absent or unusable.
 *
 * The URL is attacker-writable — these params come off a link — and the values are
 * interpolated into every iframe src and sidebar link the shell builds, so the level is
 * whitelisted and the IMOs are reduced to digits rather than trusted as given.
 */
function readVesselFromParams(params: URLSearchParams): VesselSnapshot | null {
  const id = params.get('vesselId');
  const imoList = (params.get('imoList') ?? '')
    .split(',')
    .map((imo) => imo.trim())
    .filter((imo) => /^\d+$/.test(imo));

  if (!id || imoList.length === 0) return null;

  const level = LEVELS.includes(params.get('vesselLevel') ?? '')
    ? (params.get('vesselLevel') as string)
    : 'VESSEL';

  return {
    id: id.slice(0, 128),
    vesselName: (params.get('vesselName') ?? '').slice(0, 128),
    // Groups have no single imo — they are identified by the list. A VESSEL keeps its
    // own, because cross-app restore and the sidebar's ?imo= both key off it.
    imo: level === 'VESSEL' ? imoList[0] : '',
    imoList,
    level,
  };
}

/**
 * The vessel this URL is handing over, from the top-level query or from the query of
 * the `lastVisitedRoute` value the chat replays back to us.
 */
export function readVesselHandover(search: string): VesselSnapshot | null {
  const params = new URLSearchParams(search);
  const direct = readVesselFromParams(params);
  if (direct) return direct;

  const route = params.get('lastVisitedRoute');
  if (!route) return null;

  const [, nestedQuery = ''] = route.split('?');
  return readVesselFromParams(new URLSearchParams(nestedQuery));
}

/** Write a handed-over vessel into the keys the shared VesselDropdown restores from. */
function storeVessel(vessel: VesselSnapshot): void {
  localStorage.setItem('vesselSync_activeVessel', JSON.stringify(vessel));
  localStorage.setItem('selectedUserVesselId', vessel.id);

  // The group context does not travel — a single vessel picked from inside a group
  // arrives as a bare VESSEL. Dropping any stored group is deliberate: whatever this
  // tab holds is from an unrelated earlier session, and a group that does not contain
  // the handed-over vessel filters the dropdown's own list against it.
  if (vessel.level === 'VESSEL') {
    localStorage.removeItem('vesselDropdown_activeGroup');
  } else {
    localStorage.setItem('vesselDropdown_activeGroup', JSON.stringify(vessel));
  }
}

/** Drop the handover params so a later refresh cannot re-apply a vessel the user has since changed. */
function stripVesselParams(): void {
  const url = new URL(window.location.href);
  if (!VESSEL_PARAMS.some((name) => url.searchParams.has(name))) return;
  VESSEL_PARAMS.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Apply the vessel this page was opened with, if any. Returns what was applied so the
 * caller can log it; null when the URL carries no handover.
 */
export function applyVesselHandover(): VesselSnapshot | null {
  if (typeof window === 'undefined') return null;

  try {
    const vessel = readVesselHandover(window.location.search);
    if (!vessel) return null;

    storeVessel(vessel);
    stripVesselParams();
    return vessel;
  } catch (e) {
    // The user followed a deep link naming a vessel and silently landed on a
    // different one — a wrong-data report, not an error report, without this.
    reportError(e, { event_detail: 'vessel_handover_failed' });
    return null;
  }
}

/**
 * The route with the currently selected vessel appended — what gets posted to the
 * parent chat, so that whatever it replays on the new-tab URL still names the vessel.
 * Returns the route unchanged when nothing is selected.
 */
export function withVesselParams(route: string): string {
  try {
    const raw = localStorage.getItem('vesselSync_activeVessel');
    if (!raw) return route;

    const vessel = JSON.parse(raw) as Partial<VesselSnapshot>;
    const imoList = Array.isArray(vessel.imoList) ? vessel.imoList : [];
    if (!vessel.id || imoList.length === 0) return route;

    const [path, existingQuery = ''] = route.split('?');
    const params = new URLSearchParams(existingQuery);
    params.set('vesselId', String(vessel.id));
    params.set('vesselName', String(vessel.vesselName ?? ''));
    params.set('vesselLevel', String(vessel.level ?? ''));
    params.set('imoList', imoList.join(','));

    return `${path}?${params.toString()}`;
  } catch {
    return route;
  }
}
